import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { loadIframeApi, YT_STATE, type YouTubePlayer } from '@/utils/youtube'

/**
 * A YouTube stimulus played back in the results panel, in step with the
 * timeline — the counterpart of the stage's <video> for link stimuli.
 *
 * Same clock contract as the <video>: while it plays, the player is the clock
 * and `onTick` reports its time; the panel's playhead only pushes back into the
 * player on scrubs and replays (drift past a threshold), never every frame —
 * chasing the playhead with seeks is what turned network video into a
 * slideshow before (see StimulusStage).
 *
 * The iframe never takes a click: playback is driven by the panel's own
 * transport and click-to-play overlay, so YouTube's UI can't drift the video
 * away from the playhead. Respondents saw the same full-frame player
 * (RespondentJourney's YouTubeStimulus), so the gaze dot lands on the same
 * picture they were looking at.
 *
 * `onFailed` fires when the IFrame API can't load or the video won't embed;
 * the stage then falls back to the thumbnail and the panel's wall-clock.
 */
export default function YouTubeStage({
  videoId,
  currentMs,
  playing,
  muted,
  onTick,
  onEnded,
  onFailed,
}: {
  videoId: string
  currentMs: number
  playing: boolean
  muted: boolean
  onTick?: (ms: number) => void
  onEnded?: () => void
  onFailed?: () => void
}) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const playerRef = useRef<YouTubePlayer | null>(null)
  const readyRef = useRef(false)
  // Freshest values for the player's async callbacks.
  const targetSecRef = useRef(currentMs / 1000)
  targetSecRef.current = currentMs / 1000
  const playingRef = useRef(playing)
  playingRef.current = playing
  const mutedRef = useRef(muted)
  mutedRef.current = muted
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const onFailedRef = useRef(onFailed)
  onFailedRef.current = onFailed

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    let cancelled = false
    let player: YouTubePlayer | null = null
    // YT swaps the mount node for an <iframe>; keep it outside React's tree.
    const mount = document.createElement('div')
    host.appendChild(mount)

    loadIframeApi()
      .then((api) => {
        if (cancelled) return
        player = new api.Player(mount, {
          videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            rel: 0,
            playsinline: 1,
            iv_load_policy: 3,
            start: Math.floor(targetSecRef.current),
          },
          events: {
            onReady: (e) => {
              if (cancelled) return
              playerRef.current = e.target
              readyRef.current = true
              if (mutedRef.current) e.target.mute?.()
              else e.target.unMute?.()
              e.target.seekTo?.(targetSecRef.current, true)
              if (playingRef.current) e.target.playVideo?.()
            },
            onStateChange: (e) => {
              if (e.data === YT_STATE.ENDED) onEndedRef.current?.()
            },
            onError: () => {
              if (!cancelled) onFailedRef.current?.()
            },
          },
        })
      })
      .catch(() => {
        if (!cancelled) onFailedRef.current?.()
      })

    return () => {
      cancelled = true
      readyRef.current = false
      playerRef.current = null
      try {
        player?.destroy()
      } catch {
        /* already torn down */
      }
      mount.remove()
      host.replaceChildren()
    }
  }, [videoId])

  // Play / pause follow the panel's transport.
  useEffect(() => {
    const p = playerRef.current
    if (!p || !readyRef.current) return
    if (playing) p.playVideo?.()
    else p.pauseVideo?.()
  }, [playing])

  useEffect(() => {
    const p = playerRef.current
    if (!p || !readyRef.current) return
    if (muted) p.mute?.()
    else p.unMute?.()
  }, [muted])

  // While playing, the player owns the clock (~30 Hz, like the <video>).
  useEffect(() => {
    if (!playing || !onTick) return
    const id = window.setInterval(() => {
      const t = playerRef.current?.getCurrentTime?.()
      if (readyRef.current && typeof t === 'number' && Number.isFinite(t)) onTick(t * 1000)
    }, 33)
    return () => window.clearInterval(id)
  }, [playing, onTick])

  // Scrubs and replays: park the player on the playhead. While playing the
  // playhead mirrors the player, so only a real jump crosses the threshold.
  useEffect(() => {
    const p = playerRef.current
    if (!p || !readyRef.current) return
    const t = p.getCurrentTime?.()
    if (typeof t !== 'number' || !Number.isFinite(t)) return
    const target = currentMs / 1000
    if (Math.abs(t - target) > (playing ? 0.75 : 0.25)) p.seekTo?.(target, true)
  }, [currentMs, playing])

  return (
    <Box
      ref={hostRef}
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: '#000',
        pointerEvents: 'none',
        '& iframe': { width: '100%', height: '100%', border: 0, display: 'block' },
      }}
    />
  )
}
